import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Globe, BarChart3, Search, Youtube, Facebook, Instagram, Music2, Linkedin,
  MapPin, ShoppingBag, ShoppingCart, Store, Package, Box, Plus, Trash2, Power, PowerOff
} from "lucide-react";

const ICONS = {
  globe: Globe, "bar-chart-3": BarChart3, search: Search, youtube: Youtube,
  facebook: Facebook, instagram: Instagram, "music-2": Music2, linkedin: Linkedin,
  "map-pin": MapPin, "shopping-bag": ShoppingBag, "shopping-cart": ShoppingCart,
  store: Store, package: Package, box: Box,
};

const CATEGORY_STYLE = {
  web: "text-[#007AFF]",
  seo: "text-[#00F0FF]",
  social: "text-[#B180FF]",
  local: "text-[#FFCC00]",
  marketplace: "text-[#00E676]",
};

export default function Channels() {
  const { user } = useAuth();
  const [available, setAvailable] = useState([]);
  const [channels, setChannels] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ type: "", name: "", url: "", account_id: "" });

  const canManage = user?.role === "super_admin" || user?.role === "marketing_manager";

  const load = async () => {
    const [av, ch] = await Promise.all([api.get("/channels/available"), api.get("/channels")]);
    setAvailable(av.data);
    setChannels(ch.data);
  };
  useEffect(() => { load(); }, []);

  const openAdd = (type) => {
    const meta = available.find(a => a.type === type);
    setForm({ type, name: meta?.label || "", url: "", account_id: "" });
    setShowAdd(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/channels", form);
      toast.success("Channel ditambahkan");
      setShowAdd(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal tambah channel");
    }
  };

  const toggle = async (c) => {
    await api.patch(`/channels/${c.id}`, { enabled: !c.enabled });
    toast.success(!c.enabled ? "Channel diaktifkan" : "Channel dinonaktifkan");
    load();
  };

  const del = async (c) => {
    if (!window.confirm(`Hapus channel ${c.name}?`)) return;
    await api.delete(`/channels/${c.id}`);
    toast.success("Channel dihapus");
    load();
  };

  const grouped = available.reduce((acc, a) => {
    acc[a.category] = acc[a.category] || [];
    acc[a.category].push(a);
    return acc;
  }, {});

  return (
    <div className="p-6 lg:p-8 space-y-8 fade-in">
      <div className="pb-2 border-b border-[#1F1F1F]">
        <div className="label-mono">// channel management</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2" data-testid="channels-title">
          Manajemen <span className="text-[#00F0FF]">Channel</span>
        </h1>
        <p className="text-zinc-500 text-sm mt-2">Hubungkan, aktifkan, atau nonaktifkan setiap channel digital marketing.</p>
      </div>

      {/* Connected channels */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="label-mono">// channel terhubung ({channels.length})</h2>
        </div>
        {channels.length === 0 ? (
          <div className="border border-dashed border-[#1F1F1F] p-10 text-center text-zinc-500 text-sm label-mono">
            belum ada channel terhubung
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {channels.map(c => {
              const Icon = ICONS[c.meta?.icon] || Globe;
              return (
                <div key={c.id} className="border border-[#1F1F1F] bg-[#0A0A0A] p-4" data-testid={`channel-card-${c.type}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 border border-[#1F1F1F] bg-[#050505] flex items-center justify-center">
                        <Icon size={18} className={CATEGORY_STYLE[c.meta?.category] || "text-white"} />
                      </div>
                      <div>
                        <div className="font-medium text-sm">{c.name}</div>
                        <div className="label-mono">{c.meta?.category}</div>
                      </div>
                    </div>
                    <span className={`label-mono ${c.enabled ? "text-[#00E676]" : "text-zinc-600"}`}>
                      {c.enabled ? "● aktif" : "○ nonaktif"}
                    </span>
                  </div>
                  {c.url && <div className="font-mono text-xs text-zinc-500 truncate mb-3">{c.url}</div>}
                  {canManage && (
                    <div className="flex items-center gap-2 pt-3 border-t border-[#1F1F1F]">
                      <button onClick={() => toggle(c)} data-testid={`toggle-${c.type}`}
                        className="flex-1 label-mono py-1.5 border border-[#1F1F1F] hover:border-[#007AFF] flex items-center justify-center gap-2 transition">
                        {c.enabled ? <><PowerOff size={12} /> nonaktifkan</> : <><Power size={12} /> aktifkan</>}
                      </button>
                      <button onClick={() => del(c)} data-testid={`delete-${c.type}`}
                        className="label-mono py-1.5 px-3 border border-[#1F1F1F] text-zinc-500 hover:border-[#FF3B30] hover:text-[#FF3B30] transition">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Available to add */}
      {canManage && (
        <section>
          <h2 className="label-mono mb-3">// tambah channel baru</h2>
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat} className="mb-5">
              <div className={`label-mono mb-2 ${CATEGORY_STYLE[cat]}`}>{cat}</div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {items.map(a => {
                  const Icon = ICONS[a.icon] || Globe;
                  const already = channels.some(c => c.type === a.type);
                  return (
                    <button
                      key={a.type}
                      disabled={already}
                      onClick={() => openAdd(a.type)}
                      data-testid={`add-channel-${a.type}`}
                      className={`border border-[#1F1F1F] p-3 flex items-center gap-3 text-left transition ${
                        already ? "opacity-40 cursor-not-allowed" : "hover:border-[#007AFF] hover:bg-[#0A0A0A]"
                      }`}
                    >
                      <Icon size={18} className={CATEGORY_STYLE[cat]} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate">{a.label}</div>
                        <div className="label-mono">{already ? "sudah terhubung" : "+ tambah"}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </section>
      )}

      {/* Add modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            data-testid="add-channel-modal"
            className="w-full max-w-md border border-[#1F1F1F] bg-[#0A0A0A] p-6 space-y-4">
            <div>
              <div className="label-mono">// new connection</div>
              <h3 className="font-display font-bold text-2xl mt-1">Tambah Channel</h3>
            </div>
            <div>
              <label className="label-mono block mb-2">nama channel</label>
              <input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                data-testid="modal-channel-name"
                className="w-full bg-[#050505] border border-[#1F1F1F] px-3 py-2.5 font-mono text-sm outline-none focus:border-[#007AFF]" />
            </div>
            <div>
              <label className="label-mono block mb-2">url / handle</label>
              <input value={form.url} onChange={(e) => setForm({...form, url: e.target.value})}
                data-testid="modal-channel-url"
                placeholder="https://... atau @username"
                className="w-full bg-[#050505] border border-[#1F1F1F] px-3 py-2.5 font-mono text-sm outline-none focus:border-[#007AFF]" />
            </div>
            <div>
              <label className="label-mono block mb-2">account id (opsional)</label>
              <input value={form.account_id} onChange={(e) => setForm({...form, account_id: e.target.value})}
                data-testid="modal-channel-account"
                className="w-full bg-[#050505] border border-[#1F1F1F] px-3 py-2.5 font-mono text-sm outline-none focus:border-[#007AFF]" />
            </div>
            <div className="flex items-center gap-3 pt-2">
              <button type="button" onClick={() => setShowAdd(false)}
                className="flex-1 py-2.5 border border-[#1F1F1F] label-mono hover:bg-[#0F0F0F]">batal</button>
              <button type="submit" data-testid="modal-submit-channel"
                className="flex-1 py-2.5 bg-[#007AFF] hover:bg-[#0056b3] label-mono text-white">→ tambah</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
